import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, Syringe, Heart, Baby, 
  Microscope, Scissors, Clock, ChevronRight, Phone
} from 'lucide-react';

const services = [
  {
    id: 1,
    icon: Stethoscope,
    color: 'bg-blue-100 text-blue-600',
    title: { en: 'General Medicine', fr: 'Médecine Générale' },
    description: { en: 'Primary healthcare for all ages', fr: 'Soins primaires pour tous les âges' },
    schedule: { en: '24/7 Emergency', fr: 'Urgence 24h/24' }
  },
  {
    id: 2,
    icon: Baby,
    color: 'bg-green-100 text-green-600',
    title: { en: 'Pediatrics', fr: 'Pédiatrie' },
    description: { en: 'Specialized care for children', fr: 'Soins spécialisés pour enfants' },
    schedule: { en: '24/7 Emergency', fr: 'Urgence 24h/24' }
  },
  {
    id: 3,
    icon: Heart,
    color: 'bg-red-100 text-red-600',
    title: { en: 'Maternity', fr: 'Maternité' },
    description: { en: 'Complete maternal care', fr: 'Soins maternels complets' },
    schedule: { en: '24/7 Service', fr: 'Service 24h/24' }
  },
  {
    id: 4,
    icon: Microscope,
    color: 'bg-purple-100 text-purple-600',
    title: { en: 'Laboratory', fr: 'Laboratoire' },
    description: { en: 'Medical analysis', fr: 'Analyses médicales' },
    schedule: { en: 'Mon-Sat: 7:30-16:30', fr: 'Lun-Sam: 7h30-16h30' }
  },
  {
    id: 5,
    icon: Scissors,
    color: 'bg-orange-100 text-orange-600',
    title: { en: 'Operating Block', fr: 'Bloc Opératoire' },
    description: { en: 'Surgical services', fr: 'Services chirurgicaux' },
    schedule: { en: '24/7 Available', fr: 'Disponible 24h/24' }
  },
  {
    id: 6,
    icon: Syringe,
    color: 'bg-indigo-100 text-indigo-600',
    title: { en: 'Vaccination', fr: 'Vaccination' },
    description: { en: 'Immunization services', fr: 'Services de vaccination' },
    schedule: { en: 'Mon-Sat', fr: 'Lun-Sam' }
  }
];

export default function Services() {
  const { language, t } = useLanguage();

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t('Our Expertise', 'Notre Expertise')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
            {t('Complete Medical Services', 'Services Médicaux Complets')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            {t(
              'Quality healthcare with modern equipment and experienced professionals',
              'Soins de qualité avec équipement moderne et professionnels expérimentés'
            )}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-6 sm:p-8">
                  {/* Icon */}
                  <div className={`${service.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={32} />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-blue-900 mb-2">
                    {language === 'en' ? service.title.en : service.title.fr}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {language === 'en' ? service.description.en : service.description.fr}
                  </p>

                  {/* Schedule */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Clock size={16} />
                    <span>{language === 'en' ? service.schedule.en : service.schedule.fr}</span>
                  </div>

                  {/* Link */}
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-3 transition-all"
                  >
                    {t('Learn more', 'En savoir plus')}
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Decorative bottom bar */}
                <div className="h-1 bg-gradient-to-r from-blue-600 to-amber-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition transform hover:scale-105 shadow-lg"
          >
            {t('View All Services', 'Voir Tous les Services')}
            <ChevronRight size={20} />
          </Link>
        </div>

        {/* Emergency Banner */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden">
          {/* Fixed SVG Pattern */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat'
            }}
          />
          
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">
                {t('24/7 Emergency Services', 'Services d\'Urgence 24h/24')}
              </h3>
              <p className="text-blue-100 text-lg">
                {t('Operating Block, Maternity & Pediatrics', 'Bloc Opératoire, Maternité & Pédiatrie')}
              </p>
            </div>
            <a
              href="tel:653474614"
              className="flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-amber-50 transition transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              <Phone size={20} />
              {t('Call: +237 653 474 614', 'Appel: +237 653 474 614')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}